import { produce } from "immer";
import { createCardInstance, shuffle } from './gameUtils.js';
import { PlayerId, TriggerType, CardType, EffectType } from './constants.js';
import { checkCardReaction } from './card.js';
import { _updateGameOverState } from './gameOver.js';
import LogEntryGenerator from './LogEntryGenerator.js';
import { getCardName, getUILoc } from '../utils/cardUtils.js';

const logEntryGenerator = new LogEntryGenerator();
let globalEffectLogger = null;

const addEffectLogEntry = (gameState, effectType, args, sourceCard) => {
    const logEntry = logEntryGenerator.generateEntry(effectType, args, sourceCard, gameState);
    if (logEntry) {
        gameState.game_log.push(logEntry);
    }
};

const modifyParameterCorrectionCalculation = (gameState, playerId, correctTarget, correctDirection, amount) => {
    const player = gameState.players[playerId];
    if (!player || !player.modify_parameter_corrections || player.modify_parameter_corrections.length === 0) {
        return amount;
    }
    const availableCards = Object.values(gameState.players).flatMap(p => p ? [...p.field, p.ideology] : []).filter(Boolean);

    let limit = null;
    let amplification = 0;
    let attenuation = 0;
    const usedCorrections = [];

    player.modify_parameter_corrections.forEach((correction, index) => {
        if (correction.correct_target === correctTarget && correction.correct_direction === correctDirection) {
            usedCorrections.push(index);

            availableCards.forEach(card => {
                if (card && card.instance_id === correction.source_card_id) {
                    switch (correction.correct_type) {
                        case 'limit':
                            if (limit === null || correction.amount < limit) {
                                limit = correction.amount;
                            }
                            break;
                        case 'amplification':
                            amplification += correction.amount;
                            break;
                        case 'attenuation':
                            attenuation += correction.amount;
                            break;
                        default:
                            break;
                    }
                }
            });
        }
    });

    if (usedCorrections.length === 0) {
        return amount;
    }

    let finalAmount = amount;
    const correctionValue = amplification - attenuation;
    const sign = amount >= 0 ? 1 : -1;

    finalAmount = Math.max(0, Math.abs(amount) + correctionValue) * sign;
    
    if (limit !== null) {
        if (Math.abs(finalAmount) > Math.abs(limit)) {
            finalAmount = limit * sign;
        }
    }

    const uniqueUsedIndexes = [...new Set(usedCorrections)];
    uniqueUsedIndexes.sort((a, b) => b - a).forEach(index => {
        player.modify_parameter_corrections.splice(index, 1);
    });

    return finalAmount;
};

const enforceHandLimit = (gameState, player, cardToAdd, effectsQueue, sourceCard, sourcePile) => {
    if (player.hand.length >= player.hand_capacity) {
        gameState.animation_queue.push({
            effect: {
                effect_type: 'LIMIT_WARNING',
                args: {
                    player_id: player.id,
                    limit_type: 'hand',
                    message: getUILoc('MESSAGES.LIMIT_HAND')
                }
            },
            sourceCard: null
        });
        
        player.discard.push(cardToAdd);
        cardToAdd.location = 'discard';

        const ownerPlayerId = cardToAdd.owner;
        const discardArgs = {
            card_id: cardToAdd.instance_id,
            target_card_id: cardToAdd.instance_id,
            card_type: cardToAdd.card_type,
            source_pile: sourcePile || 'hand',
            destination_pile: 'discard',
            player_id: ownerPlayerId,
            target_player_id: ownerPlayerId
        };

        effectsQueue.unshift([{ effect_type: TriggerType.CARD_DISCARDED_OWNER, args: discardArgs }, sourceCard || cardToAdd]);
        effectsQueue.unshift([{ effect_type: TriggerType.CARD_DISCARDED, args: discardArgs }, sourceCard || cardToAdd]);
        effectsQueue.unshift([{ effect_type: TriggerType.CARD_DISCARDED_THIS, args: discardArgs }, sourceCard || cardToAdd]);

        return false;
    }
    return true;
};

const enforceFieldLimit = (gameState, player, cardToMove, effectsQueue, sourceCard) => {
    if (player.field.length >= player.field_limit && cardToMove.card_type === CardType.WEALTH) {
        effectsQueue.push([{ 
            effect_type: 'LIMIT_WARNING',
            args: {
                player_id: player.id,
                limit_type: 'field',
                message: getUILoc('MESSAGES.LIMIT_FIELD')
            }
        }, sourceCard]);

        effectsQueue.push([{
            effect_type: EffectType.MOVE_CARD,
            args: {
                player_id: player.id,
                card_id: cardToMove.instance_id,
                card_to_move: cardToMove,
                source_pile: cardToMove.location,
                destination_pile: 'discard'
            }
        }, sourceCard]);
        
        return false;
    }
    return true;
};
        
const _getTargetPlayers = (gameState, selfPlayerId, targetPlayerIdStr) => {
    if (Array.isArray(targetPlayerIdStr)) {
        return targetPlayerIdStr.map(id => gameState.players[id]);
    }
    const opponentPlayerId = selfPlayerId === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
    switch (targetPlayerIdStr) {
        case 'self':
            return [gameState.players[selfPlayerId]];
        case 'opponent':
            return [gameState.players[opponentPlayerId]];
        case 'self_and_opponent':
            return [gameState.players[selfPlayerId], gameState.players[opponentPlayerId]];
        default:
            return [gameState.players[targetPlayerIdStr]]; 
    }
};

const _getCardsFromPiles = (player, source_piles, card_type) => {
      let cards = [];
      if (!player || !source_piles) return cards;

      source_piles.forEach(pileName => {
          const pile = player[pileName];
          if (pile) {
              const cardsInPile = Array.isArray(pile) ? pile : [pile];
  
              const filteredCards = cardsInPile.filter(c => {
                  const match = !card_type || c.card_type === card_type;
                  return match;
              });
              cards.push(...filteredCards);
          }
      });
      return cards;
  };

const _selectCards = (gameState, player, available_cards, selection_method, count, sourceCard, original_args) => {
    if (!available_cards || available_cards.length === 0) return [];
    const selectionCount = count != null ? count : (selection_method === 'choice' ? 1 : available_cards.length);

    switch (selection_method) {
        case 'all':
            return available_cards;
        case 'random':
            return shuffle([...available_cards]).slice(0, selectionCount);
        case 'highest_required_scale':
            return [...available_cards].sort((a, b) => b.required_scale - a.required_scale).slice(0, selectionCount);
        case 'lowest_durability':
            const numToSelect = count != null ? count : 1;
            return [...available_cards].sort((a, b) => {
                const durabilityA = a.current_durability !== undefined ? a.current_durability : a.durability;
                const durabilityB = b.current_durability !== undefined ? b.current_durability : b.durability;
                return durabilityA - durabilityB;
            }).slice(0, numToSelect);
        case 'top':
            return available_cards.slice(0, selectionCount);
        case 'bottom':
            return available_cards.slice(-selectionCount);
        case 'front':
            if (!sourceCard || !sourceCard.owner) return [];
            const ownerId = sourceCard.owner;
            const opponentId = ownerId === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
            const ownerField = gameState.players[ownerId].field;
            const opponentField = gameState.players[opponentId].field;
            
            const sourceIndex = ownerField.findIndex(c => c.instance_id === sourceCard.instance_id);
            if (sourceIndex === -1) return [];
            
            if (opponentField[sourceIndex]) {
                return [opponentField[sourceIndex]];
            }
            return [];
        case 'choice':
            gameState.awaiting_input = {
                type: 'CHOICE_CARDS_FOR_OPERATION',
                options: available_cards,
                player_id: player.id,
                source_card_instance_id: sourceCard.instance_id,
                source_card_name: getCardName(sourceCard.id),
                count: selectionCount,
                source_effect: { 
                    effect_type: EffectType.PROCESS_CARD_OPERATION,
                    args: { ...original_args, selection_method: 'pre_selected' },
                },
                is_turn_end_effect: gameState.processing_status?.is_processing_turn_end || false
            };
            return null;
        case 'pre_selected':
            const selectedCards = original_args.selected_cards;
            if (!Array.isArray(selectedCards)) {
                return [];
            }
            const selectedIds = new Set(selectedCards.map(card => 
                typeof card === 'object' && card.instance_id ? card.instance_id : card
            ));
            return available_cards.filter(card => selectedIds.has(card.instance_id));
        case 'non_money':
            // 33: マネー
            return available_cards.filter(card => card.id !== 33);
        default:
            return [];
    }
};

const syncCardDataInAllPiles = (draftState, cardId, updatedData) => {
    let cardFoundAndUpdated = false;

    if (draftState.all_card_instances[cardId]) {
        for (const key in updatedData) {
            if (draftState.all_card_instances[cardId][key] !== updatedData[key]) {
                draftState.all_card_instances[cardId][key] = updatedData[key];
                cardFoundAndUpdated = true;
            }
        }
    } else {
        return;
    }

    for (const playerId in draftState.players) {
        const player = draftState.players[playerId];
        const piles = ['hand', 'field', 'deck', 'discard'];
        let isUpdated = false;
        piles.forEach(pileName => {
            if (player[pileName] && Array.isArray(player[pileName])) {
                const cardIndex = player[pileName].findIndex(c => c.instance_id === cardId);
                if (cardIndex !== -1) {
                    for (const key in updatedData) {
                         if (player[pileName][cardIndex][key] !== updatedData[key]) {
                            player[pileName][cardIndex][key] = updatedData[key];
                            isUpdated = true;
                        }
                    }
                }
            }
        });
        cardFoundAndUpdated = isUpdated;

        if (player.ideology && player.ideology.instance_id === cardId) {
            for (const key in updatedData) {
                if (player.ideology[key] !== updatedData[key]) {
                    player.ideology[key] = updatedData[key];
                    cardFoundAndUpdated = true;
                }
            }
        }
    }

    if (cardFoundAndUpdated) {
        draftState.lastUpdate = Date.now();
    }
};

const effectHandlers = {
    [EffectType.PLAYER_ACTION]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, action_type, card_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        if (action_type === 'play_card') {
            const card = player.hand.find(c => c.instance_id === card_id);
            if (!card) return;

            player.cards_played_this_turn = (player.cards_played_this_turn || 0) + 1;

            const addEffect = (type, effectArgs, srcCard) => {
                effectsQueue.unshift([{ effect_type: type, args: effectArgs }, srcCard]);
            };
            const addEffectToBack = (type, effectArgs, srcCard) => {
                effectsQueue.push([{ effect_type: type, args: effectArgs }, srcCard]);
            }

            if (card.card_type === CardType.EVENT) {
                // 69: 官僚主義
                if (card.id !== 69) {
                    addEffect(EffectType.MOVE_CARD, {
                        card_id: card.instance_id,
                        source_pile: 'playing_event',
                        destination_pile: 'discard',
                        player_id: player_id,
                    }, card);
                }

                const playEventArgs = {
                    player_id: player_id,
                    card_id: card.instance_id,
                    target_card_id: args.target_card_id || card.instance_id
                };
                addEffect(TriggerType.PLAY_EVENT_THIS, playEventArgs, card);
                addEffect(EffectType.PRESENT_EVENT_CARD, { card_id: card.instance_id, player_id: player_id }, card);

                addEffect(EffectType.MOVE_CARD, {
                    card_id: card.instance_id,
                    source_pile: 'hand',
                    destination_pile: 'playing_event',
                    player_id: player_id,
                }, card);

                const ownerPlayerId = player_id;
                const baseEventArgs = { card_id: card.instance_id, target_card_id: card.instance_id, card_type: card.card_type };
                const ownerEventArgs = { ...baseEventArgs, player_id: ownerPlayerId, target_player_id: ownerPlayerId };

                addEffectToBack(TriggerType.PLAY_EVENT, ownerEventArgs, card);
                addEffectToBack(TriggerType.PLAY_EVENT_OWNER, ownerEventArgs, card);
                addEffectToBack(TriggerType.PLAYER_PLAY_CARD_ACTION, { player_id: player_id, action_type: 'card_played', card_id: card.instance_id, target_card_id: card.instance_id }, card);
                addEffectToBack(TriggerType.PLAYER_PLAY_CARD_ACTION_OWNER, { player_id: ownerPlayerId, action_type: 'card_played', card_id: card.instance_id }, card);
                addEffectToBack(TriggerType.PLAYER_PLAY_CARD_ACTION_OPPONENT, { player_id: ownerPlayerId, action_type: 'card_played', card_id: card.instance_id }, card);

            } else {
                addEffect(EffectType.MOVE_CARD, {
                    card_id: card.instance_id,
                    source_pile: 'hand',
                    destination_pile: card.card_type === CardType.IDEOLOGY ? 'ideology' : 'field',
                    player_id: player_id,
                }, card);
                
                const ownerPlayerId = player_id;
                addEffectToBack(TriggerType.PLAYER_PLAY_CARD_ACTION, { player_id: player_id, action_type: 'card_played', card_id: card.instance_id, target_card_id: card.instance_id }, card);
                addEffectToBack(TriggerType.PLAYER_PLAY_CARD_ACTION_OWNER, { player_id: ownerPlayerId, action_type: 'card_played', card_id: card.instance_id }, card);
                addEffectToBack(TriggerType.PLAYER_PLAY_CARD_ACTION_OPPONENT, { player_id: ownerPlayerId, action_type: 'card_played', card_id: card.instance_id }, card);
            }
            gameState.temp_effect_data["last_played_card_id"] = card.instance_id;
        }
    },
    [TriggerType.START_TURN]: () => {},
    [TriggerType.END_TURN_OWNER]: () => {},
    [TriggerType.PLAY_EVENT_THIS]: () => {},
    [TriggerType.DAMAGE_THIS]: () => {},
    [TriggerType.BOOST_THIS]: () => {},
    [TriggerType.WEALTH_DURABILITY_ZERO]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, target_player_id, card_id, target_card_id } = args;
        const eventArgs = { player_id, target_player_id, card_id, target_card_id };
        effectsQueue.unshift([{ effect_type: TriggerType.WEALTH_DURABILITY_ZERO_OPPONENT, args: eventArgs }, sourceCard]);
    },

    [EffectType.DRAW_CARD]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];

        if (player && player.deck.length > 0) {
            effectsQueue.unshift([{
                effect_type: EffectType.MOVE_CARD,
                args: {
                    player_id: player_id,
                    card_id: 'draw_from_deck',
                    source_pile: 'deck',
                    destination_pile: 'hand',
                }
            }, sourceCard]);
        }
    },

    [EffectType.ADD_CARD_TO_GAME]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, card_template_name, card_template_id, destination_pile, count = 1, position, condition_field_wealth_limit } = args;
        let { initial_durability } = args;
        const player = gameState.players[player_id];
        
        // ID優先で検索、なければ名前（後方互換性）
        const templateId = card_template_id || card_template_name;
        const cardTemplate = gameState.cardDefs[templateId];

        if (!player || !cardTemplate) return;

        if (condition_field_wealth_limit !== undefined) {
            const limit = condition_field_wealth_limit > 0 ? condition_field_wealth_limit : player.field_limit;
            const wealthOnField = player.field.filter(c => c.card_type === CardType.WEALTH).length;

            if (wealthOnField >= limit) {
                return;
            }
        }

        if (typeof initial_durability === 'string') {
            if (initial_durability === 'self_all_wealth_on_field') {
                initial_durability = player.field
                    .filter(c => c.card_type === CardType.WEALTH)
                    .reduce((sum, card) => sum + (Math.max(card.current_durability, 0)), 0);
            }
            initial_durability = Math.max(0, initial_durability);
        } else if (args.initial_durability_based_on_field_wealth_total) {
            initial_durability = player.field
                .filter(c => c.card_type === CardType.WEALTH)
                .reduce((sum, card) => sum + (card.current_durability || 0), 0);
            initial_durability = Math.max(0, initial_durability);
        }
        initial_durability = initial_durability === 0 ? null : initial_durability;

        for (let i = 0; i < count; i++) {
            const newCard = createCardInstance(cardTemplate, player_id);
            if (initial_durability === null && cardTemplate.card_type === CardType.WEALTH) continue;

            if (initial_durability !== undefined && initial_durability !== null) {
                newCard.durability = initial_durability;
                newCard.current_durability = initial_durability;
            }
            
            gameState.all_card_instances[newCard.instance_id] = newCard;

            let last_destination_pile = destination_pile;
            if (destination_pile === 'field' && player.field.length >= player.field_limit && newCard.card_type === CardType.WEALTH) {
                last_destination_pile = 'discard';
            }

            effectsQueue.unshift([{
                effect_type: EffectType.MOVE_CARD,
                args: {
                    player_id: player_id,
                    card_id: newCard.instance_id,
                    card_to_move: newCard,
                    source_pile: 'game_source',
                    destination_pile: last_destination_pile,
                    maintain: true,
                    position: position,
                    source_card_id: sourceCard ? sourceCard.instance_id : null
                }
            }, sourceCard]);
        }
    },

    [EffectType.REMOVE_CARD_FROM_GAME]: (gameState, args) => {
        const { card_id, player_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const piles = ['hand', 'deck', 'field', 'discard'];
        let cardRemoved = false;
        for (const pileName of piles) {
            const pile = player[pileName];
            if(Array.isArray(pile)){
                const originalLength = pile.length;
                const newPile = pile.filter(c => c.instance_id !== card_id);
                if (originalLength > newPile.length) {
                    player[pileName] = newPile;
                    cardRemoved = true;
                    break; 
                }
            }
        }
        if (!cardRemoved && player.ideology && player.ideology.instance_id === card_id) {
            player.ideology = null;
            cardRemoved = true;
        }
    },

    [EffectType.MODIFY_CONSCIOUSNESS]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, amount } = args;
        if (gameState.players[player_id]) {
            const originalAmount = amount;
            const finalAmount = modifyParameterCorrectionCalculation(gameState, player_id, 'consciousness', amount > 0 ? 'increase' : 'decrease', amount);
            gameState.temp_effect_data.last_consciousness_change = finalAmount;
            
            if (originalAmount < 0) {
                const tempKey = `${player_id}_last_consciousness_decrease`;
                gameState.temp_effect_data[tempKey] = Math.abs(finalAmount);
            }
            
            gameState.players[player_id].consciousness += finalAmount;
            
            if (sourceCard) {
                const cId = sourceCard.card_id || sourceCard.id;
                const cName = cId ? getCardName(cId) : (sourceCard.name || null);
                if (cName) {
                    gameState.temp_effect_data.last_consciousness_source_card = {
                        card_id: cId,
                        card_name: cName,
                        target_player_id: player_id
                    };
                }
            }

            const triggerArgs = { ...args, target_player_id: player_id };
            effectsQueue.unshift([{ effect_type: TriggerType.MODIFY_CONSCIOUSNESS, args: triggerArgs }, sourceCard]);
            
            if (finalAmount !== 0 || originalAmount !== finalAmount) {
                if (originalAmount !== 0 && finalAmount === 0) {
                    effectsQueue.push([{
                        effect_type: 'EFFECT_NULLIFIED',
                        args: {
                            target_card_id: sourceCard?.instance_id,
                            reason: 'correction_nullified',
                            message: getUILoc('MESSAGES.NULLIFIED_CONSCIOUSNESS')
                        }
                    }, sourceCard]);
                }
                
                const changeArgs = {
                    player_id,
                    original_amount: originalAmount,
                    actual_amount: finalAmount,
                    source_card_id: args.source_card_id || (sourceCard ? sourceCard.instance_id : null)
                };
                addEffectLogEntry(gameState, EffectType.CONSCIOUSNESS_CHANGED, changeArgs, sourceCard);
                
                gameState.animation_queue.push({
                    effect: {
                        effect_type: EffectType.CONSCIOUSNESS_CHANGED,
                        args: changeArgs
                    },
                    sourceCard: sourceCard
                });

                effectsQueue.unshift([{ effect_type: EffectType.CONSCIOUSNESS_CHANGED, args: changeArgs }, sourceCard]);
                effectsQueue.unshift([{ effect_type: EffectType.CHECK_GAME_OVER, args: {} }, sourceCard]);
            }
        }
    },

    [EffectType.MODIFY_SCALE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, amount } = args;
        if (gameState.players[player_id]) {
            const originalAmount = amount;
            const finalAmount = modifyParameterCorrectionCalculation(gameState, player_id, 'scale', amount > 0 ? 'increase' : 'decrease', amount);
            gameState.temp_effect_data.last_scale_change = finalAmount;
            gameState.players[player_id].scale = Math.max(0, gameState.players[player_id].scale + finalAmount);
            
            if (finalAmount !== 0 || originalAmount !== finalAmount) {
                if (originalAmount !== 0 && finalAmount === 0) {
                    effectsQueue.push([{
                        effect_type: 'EFFECT_NULLIFIED',
                        args: {
                            target_card_id: sourceCard?.instance_id,
                            reason: 'correction_nullified',
                            message: getUILoc('MESSAGES.NULLIFIED_SCALE')
                        }
                    }, sourceCard]);
                }
                
                const changeArgs = {
                    player_id,
                    original_amount: originalAmount,
                    actual_amount: finalAmount,
                    source_card_id: args.source_card_id || (sourceCard ? sourceCard.instance_id : null)
                };
                addEffectLogEntry(gameState, EffectType.SCALE_CHANGED, changeArgs, sourceCard);
                
                gameState.animation_queue.push({
                    effect: {
                        effect_type: EffectType.SCALE_CHANGED,
                        args: changeArgs
                    },
                    sourceCard: sourceCard
                });

                effectsQueue.unshift([{ effect_type: EffectType.SCALE_CHANGED, args: changeArgs }, sourceCard]);
            }
        }
    },

    [EffectType.SET_CONSCIOUSNESS]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, amount_percentage, round_down = false } = args;
        const amount = Number(args.amount || 0);
        const player = gameState.players[player_id];
        if (player) {
            const oldValue = player.consciousness || 0;
            let finalValue = amount;
            if (amount_percentage !== undefined) {
                const calcValue = oldValue * (Number(amount_percentage) / 100);
                finalValue = (round_down ? Math.floor(calcValue) : Math.round(calcValue)) + amount;
            }
            const newValue = Math.max(0, Math.round(finalValue) || 0);
            player.consciousness = newValue;
            
            if (sourceCard) {
                const cId = sourceCard.card_id || sourceCard.id;
                const cName = cId ? getCardName(cId) : (sourceCard.name || null);
                if (cName) {
                    gameState.temp_effect_data.last_consciousness_source_card = {
                        card_id: cId,
                        card_name: cName,
                        target_player_id: player_id
                    };
                }
            }

            const diff = newValue - oldValue;
            const changeArgs = { player_id, original_amount: diff, actual_amount: diff, is_set: true, source_card_id: args.source_card_id || (sourceCard ? sourceCard.instance_id : null) };
            addEffectLogEntry(gameState, EffectType.SET_CONSCIOUSNESS, args, sourceCard);
            addEffectLogEntry(gameState, EffectType.CONSCIOUSNESS_CHANGED, changeArgs, sourceCard);
            effectsQueue.unshift([{ effect_type: EffectType.CONSCIOUSNESS_CHANGED, args: changeArgs }, sourceCard]);
            effectsQueue.unshift([{ effect_type: EffectType.CHECK_GAME_OVER, args: {} }, sourceCard]);
        }
    },

    [EffectType.SET_SCALE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, amount_percentage, round_down = false } = args;
        const amount = Number(args.amount || 0);
        const player = gameState.players[player_id];
        if (player) {
            const oldValue = player.scale || 0;
            let finalValue = amount;
            if (amount_percentage !== undefined) {
                const calcValue = oldValue * (Number(amount_percentage) / 100);
                finalValue = (round_down ? Math.floor(calcValue) : Math.round(calcValue)) + amount;
            }
            const newValue = Math.max(0, Math.round(finalValue) || 0);
            player.scale = newValue;

            const diff = newValue - oldValue;
            const changeArgs = { player_id, original_amount: diff, actual_amount: diff, is_set: true, source_card_id: args.source_card_id || (sourceCard ? sourceCard.instance_id : null) };
            addEffectLogEntry(gameState, EffectType.SET_SCALE, args, sourceCard);
            addEffectLogEntry(gameState, EffectType.SCALE_CHANGED, changeArgs, sourceCard);
            effectsQueue.unshift([{ effect_type: EffectType.SCALE_CHANGED, args: changeArgs }, sourceCard]);
        }
    },

    [EffectType.MODIFY_CARD_DURABILITY]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        let resolved_card_id = args.card_id;
        const playerContext = gameState.players[sourceCard ? sourceCard.owner : args.player_id];
        const originalAmount = args.amount || 0;

        if (typeof args.card_id === 'string') {
            if (args.card_id === 'self_money_on_field' && sourceCard) {
                if (playerContext) {
                    // 33: マネー
                    const moneyCard = playerContext.field.find(c => c.id === 33);
                    if (moneyCard) {
                        resolved_card_id = moneyCard.instance_id;
                    } else {
                        resolved_card_id = null;
                    }
                } else { resolved_card_id = null; }
            } else if (args.card_id === 'front' && sourceCard) {
                if (playerContext) {
                    const opponent = gameState.players[playerContext.id === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1];
                    if (opponent && sourceCard) {
                        const card_index_on_field = playerContext.field.findIndex(c => c.instance_id === sourceCard.instance_id);
                        if (card_index_on_field !== -1 && opponent.field.length > card_index_on_field) {
                            resolved_card_id = opponent.field[card_index_on_field].instance_id;
                        } else {
                            resolved_card_id = null;
                        }
                    } else { resolved_card_id = null; }
                } else { resolved_card_id = null; }
            } else if (args.card_id === 'left_opponent' && sourceCard) {
                if (playerContext) {
                    const opponent = gameState.players[playerContext.id === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1];
                    if (opponent && opponent.field.length > 0) {
                        const targetCard = opponent.field[0];
                        if (targetCard) {
                            resolved_card_id = targetCard.instance_id;
                        } else {
                            resolved_card_id = null;
                        }
                    } else { resolved_card_id = null; }
                } else { resolved_card_id = null; }
            }
        }

        const targetCardRef = gameState.all_card_instances[resolved_card_id];

        if (!targetCardRef) {
            if (sourceCard) {
                effectsQueue.unshift([{
                    effect_type: TriggerType.FAILED_PROCESS,
                    args: { player_id: sourceCard.owner, card_id: sourceCard.instance_id, target_card_id: args.card_id }
                }, sourceCard]);
            }
            return;
        }

        let baseAmount = originalAmount;
        if (args.amount_percentage !== undefined) {
            const currentDurability = targetCardRef.current_durability !== undefined ? targetCardRef.current_durability : targetCardRef.durability;
            const calcAmount = currentDurability * (Math.abs(args.amount_percentage) / 100);
            const roundedChange = args.round_down ? Math.floor(calcAmount) : Math.round(calcAmount);
            baseAmount = (args.amount_percentage < 0 ? -roundedChange : roundedChange) + originalAmount;
        }

        const triggerArgsForOwnerReserve = { ...args, amount: baseAmount, card_id: resolved_card_id, target_card_id: resolved_card_id, target_player_id: targetCardRef.owner };
        if (baseAmount > 0) {
            effectsQueue.unshift([{ effect_type: TriggerType.MODIFY_CARD_DURABILITY_INCREASE_RESERVE_OWNER, args: triggerArgsForOwnerReserve }, sourceCard]);
        } else if (baseAmount < 0) {
            effectsQueue.unshift([{ effect_type: TriggerType.MODIFY_CARD_DURABILITY_DECREASE_RESERVE_OWNER, args: triggerArgsForOwnerReserve }, sourceCard]);
        }

        if (targetCardRef && (targetCardRef.location === 'field' || targetCardRef.location === 'ideology')) {
            const finalAmount = modifyParameterCorrectionCalculation(gameState, targetCardRef.owner, 'wealth', baseAmount > 0 ? 'increase' : 'decrease', baseAmount);
            
            const currentDurability = targetCardRef.current_durability !== undefined ? targetCardRef.current_durability : targetCardRef.durability;
            const newDurability = Math.round(currentDurability + finalAmount);

            syncCardDataInAllPiles(gameState, resolved_card_id, { current_durability: newDurability });
            
            if (finalAmount !== 0 || baseAmount !== finalAmount) {
                if (baseAmount !== 0 && finalAmount === 0) {
                    effectsQueue.push([{
                        effect_type: 'EFFECT_NULLIFIED',
                        args: { target_card_id: resolved_card_id, reason: 'correction_nullified', message: getUILoc('MESSAGES.NULLIFIED_WEALTH') }
                    }, sourceCard]);
                }
                
                const changeArgs = {
                    card_id: resolved_card_id,
                    original_amount: baseAmount,
                    actual_amount: finalAmount,
                    source_card_id: args.source_card_id || (sourceCard ? sourceCard.instance_id : null),
                    new_durability: newDurability
                };
                addEffectLogEntry(gameState, EffectType.CARD_DURABILITY_CHANGED, changeArgs, sourceCard);
                
                gameState.animation_queue.push({
                    effect: {
                        effect_type: EffectType.CARD_DURABILITY_CHANGED,
                        args: changeArgs
                    },
                    sourceCard: sourceCard
                });
                
                effectsQueue.unshift([{ effect_type: EffectType.CARD_DURABILITY_CHANGED, args: changeArgs }, sourceCard]);
            }
            if (newDurability <= 0) {
                const ownerPlayerId = targetCardRef.owner;
                const baseArgs = {
                    player_id: ownerPlayerId,
                    card_id: resolved_card_id,
                    target_card_id: resolved_card_id,
                };
                const ownerArgs = { ...baseArgs, target_player_id: ownerPlayerId };

                effectsQueue.unshift(
                    [{ effect_type: TriggerType.WEALTH_DURABILITY_ZERO, args: ownerArgs }, targetCardRef],
                    [{ effect_type: TriggerType.WEALTH_DURABILITY_ZERO_OWNER, args: ownerArgs }, targetCardRef],
                    [{ effect_type: TriggerType.WEALTH_DURABILITY_ZERO_THIS, args: ownerArgs }, targetCardRef],
                    [{ effect_type: EffectType.MOVE_CARD, args: { player_id: ownerPlayerId, card_id: resolved_card_id, source_pile: 'field', destination_pile: 'discard', source_card_id: null } }, targetCardRef]
                );
            }

            if (finalAmount < 0) {
                effectsQueue.unshift([{ 
                    effect_type: TriggerType.DAMAGE_THIS, 
                    args: { 
                        damaged_card_id: resolved_card_id, 
                        damage_amount: finalAmount,
                        source_card_id: sourceCard ? sourceCard.instance_id : null,
                        target_card_id: resolved_card_id
                    }, 
                    target_card_id: resolved_card_id 
                }, sourceCard]);
            } else if (finalAmount > 0) {
                effectsQueue.unshift([{ 
                    effect_type: TriggerType.BOOST_THIS,
                    args: { 
                        boosted_card_id: resolved_card_id, 
                        boost_amount: finalAmount,
                        source_card_id: sourceCard ? sourceCard.instance_id : null,
                        target_card_id: resolved_card_id
                    }, 
                    target_card_id: resolved_card_id 
                }, sourceCard]);
            }
            if (sourceCard) {
                effectsQueue.unshift([{
                    effect_type: TriggerType.SUCCESS_PROCESS,
                    args: { player_id: sourceCard.owner, card_id: sourceCard.instance_id, target_card_id: resolved_card_id }
                }, sourceCard]);
            }
        } else {
            if (sourceCard) {
                effectsQueue.unshift([{
                    effect_type: TriggerType.FAILED_PROCESS,
                    args: { player_id: sourceCard.owner, card_id: sourceCard.instance_id, target_card_id: resolved_card_id }
                }, sourceCard]);
            }
        }
    },

    [EffectType.MODIFY_CARD_REQUIRED_SCALE]: (draftState, args) => {
        const { card_id, amount, min_value = 0, set_value = false } = args;
        const targetCard = draftState.all_card_instances[card_id];

        if (targetCard) {
            let new_scale;
            if (set_value) {
                new_scale = Math.max(min_value, amount);
            } else {
                new_scale = Math.max(min_value, targetCard.required_scale + amount);
            }
            
            syncCardDataInAllPiles(draftState, card_id, { required_scale: new_scale });
        }
    },

    [EffectType.MODIFY_FIELD_LIMIT]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, amount } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const new_limit = player.field_limit + amount;
        const current_field_cards = player.field.length;

        if (amount < 0) {
            if (new_limit < current_field_cards) {
                if (sourceCard) {
                    effectsQueue.unshift([{
                        effect_type: TriggerType.FAILED_PROCESS,
                        args: { player_id: sourceCard.owner, card_id: sourceCard.instance_id, target_card_id: sourceCard.instance_id }
                    }, sourceCard]);
                }
                return;
            } else {
                if (sourceCard) {
                    effectsQueue.unshift([{
                        effect_type: TriggerType.SUCCESS_PROCESS,
                        args: { player_id: sourceCard.owner, card_id: sourceCard.instance_id, target_card_id: sourceCard.instance_id }
                    }, sourceCard]);
                }
            }
        }
        player.field_limit = Math.max(0, new_limit);
    },

    [EffectType.MOVE_CARD]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        let { player_id, card_id, source_pile, destination_pile, maintain = false } = args;
        let cardToMove = args.card_to_move;

        const destination_player_id = args.target_player_id || player_id;
        const destination_player = gameState.players[destination_player_id];
        if (!destination_player) return;

        if (card_id === 'draw_from_deck') {
            const player = gameState.players[player_id];
            if (player && player.deck.length > 0) {
                const drawnCard = player.deck.shift();
                args.card_id = drawnCard.instance_id;
                card_id = drawnCard.instance_id;
                cardToMove = drawnCard;
            } else {
                return; 
            }
        }

        if (source_pile === 'discard_pile') source_pile = 'discard';
        if (destination_pile === 'discard_pile') destination_pile = 'discard';

        let originalLocation = null;

        if (!cardToMove && source_pile !== 'game_source') {
            const sourcePlayer = gameState.players[player_id];
            if (sourcePlayer) {
                if (source_pile === 'playing_event') {
                    cardToMove = (sourceCard && sourceCard.instance_id === card_id) ? sourceCard : null;
                } else if (source_pile === 'ideology') {
                    if (sourcePlayer.ideology && sourcePlayer.ideology.instance_id === card_id) {
                        cardToMove = sourcePlayer.ideology;
                        sourcePlayer.ideology = null;
                    } else if (gameState.displaced_ideology && gameState.displaced_ideology.instance_id === card_id) {
                        cardToMove = gameState.displaced_ideology;
                        gameState.displaced_ideology = null;
                    }
                } else if (source_pile === 'field') {
                    let cardIndex = sourcePlayer.field.findIndex(c => c.instance_id === card_id);
                    if (cardIndex > -1) {
                        cardToMove = sourcePlayer.field.splice(cardIndex, 1)[0];
                    } else if (sourcePlayer.ideology && sourcePlayer.ideology.instance_id === card_id) {
                        cardToMove = sourcePlayer.ideology;
                        sourcePlayer.ideology = null;
                    }
                } else if (sourcePlayer[source_pile] && Array.isArray(sourcePlayer[source_pile])) {
                    const pile = sourcePlayer[source_pile];
                    const cardIndex = pile.findIndex(c => c.instance_id === card_id);
                    if (cardIndex > -1) {
                        cardToMove = pile.splice(cardIndex, 1)[0];
                    }
                }
            }
        }

        if (!cardToMove) return;

        if (source_pile === 'deck') {
            effectsQueue.unshift([{ effect_type: EffectType.CHECK_GAME_OVER, args: {} }, sourceCard]);
        }

        if (source_pile !== 'game_source') {
            originalLocation = cardToMove.location;
        }
        
        cardToMove.location = destination_pile;
        cardToMove.owner = args.target_player_id || player_id;

        // Flag exposed cards as 'was_played' if they move to a "played" area
        const playedPiles = ['field', 'ideology', 'playing_event'];
        if (playedPiles.includes(destination_pile)) {
            const ownerPlayer = gameState.players[cardToMove.owner];
            if (ownerPlayer && ownerPlayer.exposed_cards) {
                const exposedCard = ownerPlayer.exposed_cards.find(c => c.id === cardToMove.id);
                if (exposedCard) {
                    exposedCard.was_played = true;
                }
            }
        }

        const isDraw = source_pile === 'deck' && destination_pile === 'hand';
        const isPlay = destination_pile === 'field' || destination_pile === 'ideology';

        if (!maintain && !isDraw && !isPlay) {
            const cardTemplate = gameState.cardDefs[cardToMove.id];
            if (cardTemplate) {
                const updates = {};
                // 33: マネー, 75: 結束主義
                const isMoneyMovingFromFieldToHand = cardToMove.id === 33 && source_pile === 'field' && destination_pile === 'hand';
                const isCard75 = cardToMove.id === 75;

                if (cardToMove.card_type === 1 && cardTemplate.durability !== undefined && !isMoneyMovingFromFieldToHand) {
                    updates.durability = cardTemplate.durability;
                    updates.current_durability = cardTemplate.durability;
                }
                if (cardTemplate.required_scale !== undefined && !isCard75) {
                    updates.required_scale = cardTemplate.required_scale;
                }

                if (Object.keys(updates).length > 0) {
                    Object.assign(cardToMove, updates);
                    syncCardDataInAllPiles(gameState, cardToMove.instance_id, updates);
                }
            }
        }

        if (destination_pile === 'ideology' || (destination_pile === 'field' && cardToMove.card_type === 3)) {
            if (destination_player.ideology &&
                destination_player.ideology.instance_id !== cardToMove.instance_id &&
                destination_player.ideology.id === cardToMove.id) {
                effectsQueue.unshift([{
                    effect_type: EffectType.MODIFY_CONSCIOUSNESS,
                    args: {
                        player_id: destination_player.id,
                        amount: 1
                    }
                }, cardToMove]);
            }

            const placeIdeologyEffect = {
                effect_type: EffectType._PLACE_IDEOLOGY,
                args: {
                    player_id: destination_player_id,
                    card_id: cardToMove.instance_id,
                }
            };

            if (destination_player.ideology && destination_player.ideology.instance_id !== cardToMove.instance_id) {
                const oldIdeology = destination_player.ideology;
                const discardOldIdeoEffect = {
                    effect_type: EffectType.MOVE_CARD,
                    args: {
                        player_id: destination_player_id,
                        card_id: oldIdeology.instance_id,
                        source_pile: 'ideology',
                        destination_pile: 'discard',
                    }
                };
                effectsQueue.unshift([placeIdeologyEffect, cardToMove]);
                effectsQueue.unshift([discardOldIdeoEffect, oldIdeology]);
            } else {
                effectsQueue.unshift([placeIdeologyEffect, cardToMove]);
            }
            return; 
        } else if (destination_pile === 'field') {
            // 33: マネー
            if (cardToMove.id !== 33 && !enforceFieldLimit(gameState, destination_player, cardToMove, effectsQueue, sourceCard)) {
                return;
            }
            destination_player.field.push(cardToMove);
            cardToMove.location = 'field';
        } else if (destination_pile === 'playing_event') {
            cardToMove.location = 'playing_event';
        } else if (destination_player[destination_pile] && Array.isArray(destination_player[destination_pile])) {
            if (destination_pile === 'hand') {
                if (enforceHandLimit(gameState, destination_player, cardToMove, effectsQueue, sourceCard, source_pile)) {
                    destination_player.hand.push(cardToMove);
                }
            } else {
                const destinationPile = destination_player[destination_pile];
                let { position } = args;

                if (!position) {
                    position = destination_pile === 'deck' ? 'random' : 'bottom';
                }

                switch (position) {
                    case 'top':
                        destinationPile.unshift(cardToMove);
                        break;
                    case 'random':
                        const randomIndex = Math.floor(Math.random() * (destinationPile.length + 1));
                        destinationPile.splice(randomIndex, 0, cardToMove);
                        break;
                    case 'bottom':
                    default:
                        destinationPile.push(cardToMove);
                        break;
                }
            }
        } else {
            if (originalLocation && destination_player[originalLocation]) {
                 destination_player[originalLocation].push(cardToMove);
                 cardToMove.location = originalLocation;
            }
            return;
        }

        if (gameState.all_card_instances[cardToMove.instance_id]) {
            gameState.all_card_instances[cardToMove.instance_id].location = cardToMove.location;
        }

        const ownerPlayerId = cardToMove.owner;
        const opponentPlayerId = ownerPlayerId === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1;

        const baseEventArgs = {
            card_id: cardToMove.instance_id,
            target_card_id: cardToMove.instance_id,
            card_type: cardToMove.card_type,
            source_pile: source_pile,
            destination_pile: destination_pile,
        };

        const ownerEventArgs = { ...baseEventArgs, player_id: ownerPlayerId, target_player_id: ownerPlayerId };
        const opponentEventArgs = { ...baseEventArgs, player_id: ownerPlayerId, target_player_id: opponentPlayerId };

        const addEffect = (type, args) => {
            effectsQueue.unshift([{ effect_type: type, args: args }, sourceCard || cardToMove]);
        };

        if (destination_pile === 'field' || destination_pile === 'ideology') {
            addEffect(TriggerType.CARD_PLACED_THIS, ownerEventArgs);
            addEffect(TriggerType.CARD_PLACED, ownerEventArgs);
            addEffect(TriggerType.CARD_PLACED_OWNER, ownerEventArgs);
            addEffect(TriggerType.CARD_PLACED_OPPONENT, opponentEventArgs);
            
            // 1: 財
            if (destination_pile === 'field' && cardToMove.card_type === 1 && destination_player.field.length >= destination_player.field_limit) {
                gameState.animation_queue.push({
                    effect: {
                        effect_type: 'LIMIT_WARNING',
                        args: {
                            player_id: destination_player.id,
                            limit_type: 'field',
                            message: getUILoc('MESSAGES.FIELD_FULL_NOTIFICATION')
                        }
                    },
                    sourceCard: null
                });
            }
        }

        if (destination_pile === 'hand') {
            addEffect(TriggerType.CARD_ADDED_TO_HAND_THIS, ownerEventArgs);
            addEffect(TriggerType.CARD_ADDED_TO_HAND, ownerEventArgs);
            addEffect(TriggerType.CARD_ADDED_TO_HAND_OWNER, ownerEventArgs);

            if (source_pile === 'deck') {
                addEffect(TriggerType.CARD_DRAWN_THIS, ownerEventArgs);
                addEffect(TriggerType.CARD_DRAWN, ownerEventArgs);
                addEffect(TriggerType.CARD_DRAWN_OWNER, ownerEventArgs);
            }
        } 
        
        if (destination_pile === 'discard' && source_pile !== 'discard') {
            addEffect(TriggerType.CARD_DISCARDED_THIS, ownerEventArgs);
            addEffect(TriggerType.CARD_DISCARDED, ownerEventArgs);
            addEffect(TriggerType.CARD_DISCARDED_OWNER, ownerEventArgs);
            addEffect(TriggerType.CARD_DISCARDED_OPPONENT, opponentEventArgs);
        }
    },

    [EffectType._PLACE_IDEOLOGY]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, card_id } = args;
        const player = gameState.players[player_id];
        const cardToPlace = gameState.all_card_instances[card_id];

        if (!player || !cardToPlace) return;

        player.ideology = cardToPlace;
        cardToPlace.location = 'ideology';

        if (gameState.all_card_instances[cardToPlace.instance_id]) {
            gameState.all_card_instances[cardToPlace.instance_id].location = cardToPlace.location;
        }

        const ownerPlayerId = cardToPlace.owner;
        const opponentPlayerId = ownerPlayerId === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
        const baseEventArgs = {
            card_id: cardToPlace.instance_id,
            target_card_id: cardToPlace.instance_id,
            card_type: cardToPlace.card_type,
            source_pile: 'ideology',
            destination_pile: 'ideology',
        };
        const ownerEventArgs = { ...baseEventArgs, player_id: ownerPlayerId, target_player_id: ownerPlayerId };
        const opponentEventArgs = { ...baseEventArgs, player_id: ownerPlayerId, target_player_id: opponentPlayerId };
        
        const addEffect = (type, args, effectSourceCard) => {
            effectsQueue.unshift([{ effect_type: type, args: args }, effectSourceCard]);
        };

        addEffect(TriggerType.CARD_PLACED_THIS, ownerEventArgs, cardToPlace);
        addEffect(TriggerType.CARD_PLACED, ownerEventArgs, cardToPlace);
        addEffect(TriggerType.CARD_PLACED_OWNER, ownerEventArgs, cardToPlace);
        addEffect(TriggerType.CARD_PLACED_OPPONENT, opponentEventArgs, cardToPlace);
    },

    [EffectType.PROCESS_CARD_OPERATION]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        if (args.target_card_id === 'last_added_card') {
            if (args.triggering_effect_args && args.triggering_effect_args.card_id) {
                args.target_card_id = args.triggering_effect_args.card_id;
            } else {
                return;
            }
        }

        let { operation, source_piles, source_pile, card_type, selection_method, count } = args;

        if (operation === 'modify_durability' && (selection_method === 'front' || selection_method === 'left_opponent')) {
            effectsQueue.unshift([{ 
                effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE, 
                args: { 
                    ...args,
                    card_id: selection_method,
                }
            }, sourceCard]);
            return;
        }

        if (source_pile === 'discard_pile') source_pile = 'discard';
        if (source_piles) {
            source_piles = source_piles.map(p => p === 'discard_pile' ? 'discard' : p);
        }

        const player_id = args.player_id || (sourceCard ? sourceCard.owner : null);
        const target_player_id = args.target_player_id || player_id;
        if (!player_id) return;

        const targetPlayers = _getTargetPlayers(gameState, player_id, target_player_id);
        const sourcePlayer = _getTargetPlayers(gameState, player_id, 'self')[0];

        for (const player of targetPlayers) {
            if (!player) continue;

            let targetCards = [];

            if (args.target_card_id) {
                const foundCard = Object.values(gameState.players)
                    .flatMap(p => [p.ideology, ...p.hand, ...p.deck, ...p.field, ...p.discard])
                    .find(c => c && c.instance_id === args.target_card_id);
                
                if (foundCard && foundCard.owner === player.id) {
                    targetCards.push(foundCard);
                }
            } else {
                if (source_pile && !source_piles) {
                    source_piles = [source_pile];
                } else if (!source_piles) {
                    source_piles = ['field'];
                }

                let availableCards = _getCardsFromPiles(player, source_piles, card_type);
                if (sourceCard) {
                    availableCards = availableCards.filter(c => c.instance_id !== sourceCard.instance_id);
                }
                if (args.triggering_effect_args && args.triggering_effect_args.card_id) {
                    if (!source_piles.includes('hand')) {
                        availableCards = availableCards.filter(c => c.instance_id !== args.triggering_effect_args.card_id);
                    }
                }

                let selectionPlayer = player;
                if (selection_method === 'choice') {
                    selectionPlayer = sourcePlayer;
                }
                const selected = _selectCards(gameState, selectionPlayer, availableCards, selection_method, count, sourceCard, args);
                if (selected === null) return;
                targetCards = selected;
            }

            if (targetCards.length === 0) continue;

            if (args.store_count_key) {
                gameState.temp_effect_data[args.store_count_key] = targetCards.length;
            }

            switch (operation) {
                case 'modify_durability': {
                    const { amount } = args;
                    for (const card of targetCards) {
                        effectsQueue.unshift([{ effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE, args: { card_id: card.instance_id, amount: amount, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
                    }
                    break;
                }
                case 'modify_required_scale': {
                    const { amount, min_value = 0 } = args;
                    for (const card of targetCards) {
                        effectsQueue.unshift([{ effect_type: EffectType.MODIFY_CARD_REQUIRED_SCALE, args: { card_id: card.instance_id, amount: amount, min_value: min_value, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
                    }
                    break;
                }
                case 'move':
                case 'move_card': {
                    const { destination_pile, position, maintain = false } = args;
                    for (const card of targetCards) {
                        const actual_source_pile = card.location || (source_piles ? source_piles[0] : 'field');
                        effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player.id, card_id: card.instance_id, source_pile: actual_source_pile, destination_pile: destination_pile, position: position, maintain: maintain, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
                    }
                    break;
                }
                case 'remove': {
                    const uniqueCards = [...new Set(targetCards)];
                    for (const card of uniqueCards) {
                        effectsQueue.unshift([{ effect_type: EffectType.REMOVE_CARD_FROM_GAME, args: { player_id: player.id, card_id: card.instance_id, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
                    }
                    break;
                }
                case 'generate_card': {
                    const { card_template_id, card_template_name, destination_pile, count = 1 } = args;
                    effectsQueue.unshift([{ effect_type: EffectType.ADD_CARD_TO_GAME, args: { player_id: player.id, card_template_id, card_template_name, destination_pile, count, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
                    break;
                }
                default:
            }
        }
    },

    [EffectType.PROCESS_DEAL_DAMAGE_TO_ALL_WEALTH]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        let { player_ids, amount, next_effect } = args;
        if (!player_ids || !amount) return;

        if (amount === 'lowest_durability_on_field') {
            let minDurability = Infinity;
            const targetIds = Array.isArray(player_ids) ? player_ids : [player_ids];
            
            targetIds.forEach(pId => {
                const player = gameState.players[pId];
                if (player && player.field) {
                    player.field.forEach(card => {
                        if (card.card_type === CardType.WEALTH) {
                            if (card.current_durability < minDurability) {
                                minDurability = card.current_durability;
                            }
                        }
                    });
                }
            });
            amount = minDurability === Infinity ? 0 : minDurability;
        }

        const operationArgs = {
            player_id: sourceCard ? sourceCard.owner : null,
            operation: 'modify_durability',
            target_player_id: player_ids,
            selection_method: 'all',
            source_piles: ['field'],
            card_type: CardType.WEALTH,
            amount: -amount
        };

        if (next_effect) {
            effectsQueue.unshift([next_effect, sourceCard]);
        }

        effectsQueue.unshift([{
            effect_type: EffectType.PROCESS_CARD_OPERATION,
            args: operationArgs
        }, sourceCard]);
    },
    [EffectType.PROCESS_CHOOSE_AND_DISCARD_IDEOLOGY]: (gameState, args, cardDefs, sourceCard) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        const opponent = gameState.players[player_id === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1];
        
        const options = [];
        if (opponent.ideology) options.push(opponent.ideology);
        if (player.ideology) options.push(player.ideology);

        if (options.length > 0) {
            gameState.awaiting_input = {
                type: 'CHOICE_CARD_FOR_EFFECT',
                options: options,
                player_id: player_id,
                source_card_instance_id: sourceCard.instance_id,
                source_card_name: getCardName(sourceCard.id),
                source_effect: {
                    effect_type: 'PROCESS_CHOOSE_AND_DISCARD_IDEOLOGY_RESOLVED',
                    args: {}
                }
            };
        }
    },
    PROCESS_CHOOSE_AND_DISCARD_IDEOLOGY_RESOLVED: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { card_id } = args;
        let targetPlayerId = null;
        
        for (const pId of [PlayerId.PLAYER1, PlayerId.PLAYER2]) {
            const player = gameState.players[pId];
            if (player.ideology && player.ideology.instance_id === card_id) {
                targetPlayerId = pId;
                break;
            }
        }

        if (targetPlayerId) {
            effectsQueue.unshift([{
                effect_type: EffectType.MOVE_CARD,
                args: {
                    player_id: targetPlayerId,
                    card_id: card_id,
                    source_pile: 'ideology',
                    destination_pile: 'discard'
                }
            }, sourceCard]);
        }
    },
    [EffectType.PROCESS_DISCARD_ALL_HAND_WEALTH_CARDS_AND_DRAW]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const cardsToDiscard = player.hand.filter(c => c.card_type === CardType.WEALTH);
        const discardedCount = cardsToDiscard.length;

        if (discardedCount > 0) {
            for (let i = 0; i < discardedCount; i++) {
                effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: 'draw_from_deck', source_pile: 'deck', destination_pile: 'hand', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
            for (const card of cardsToDiscard) {
                effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: card.instance_id, source_pile: 'hand', destination_pile: 'discard', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
        }
    },
    [EffectType.PROCESS_MOVE_HAND_WEALTH_TO_DECK_AND_DRAW]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const cardsToMove = player.hand.filter(c => c.card_type === CardType.WEALTH);
        const movedCount = cardsToMove.length;

        if (movedCount > 0) {
            for (let i = 0; i < movedCount; i++) {
                effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: 'draw_from_deck', source_pile: 'deck', destination_pile: 'hand', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
            for (const card of cardsToMove) {
                effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: card.instance_id, source_pile: 'hand', destination_pile: 'deck', position: 'random', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
        }
    },
    [EffectType.PROCESS_EXPOSE_CARD_BY_TYPE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, source_piles, card_type, card_id, count = 1 } = args;
        const player = gameState.players[player_id];
        if (!player) {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
            return;
        }

        const candidateCards = _getCardsFromPiles(player, source_piles, card_type);

        if (candidateCards.length > 0) {
            let exposedCards = [];
            let specificCardExposed = false;
            if (card_id && card_id !== 'self') {
                specificCardExposed = true;
                const specificCard = candidateCards.find(c => c.id === card_id);
                if (specificCard) {
                    exposedCards.push(specificCard);
                }
            }
            else {
                exposedCards = shuffle(candidateCards).slice(0, count);
            }
            args.exposed_cards = exposedCards;
            
            // Record exposed cards for the player
            if (!player.exposed_cards) player.exposed_cards = [];
            exposedCards.forEach(card => {
                if (!player.exposed_cards.some(c => c.id === card.id)) {
                    player.exposed_cards.push({ ...card, exposed_turn: gameState.turn_number });
                }
            });

            if (!gameState.exposed_cards) gameState.exposed_cards = [];
            gameState.exposed_cards.push(...exposedCards.map(c => ({...c, exposed_by: player.id})));
            
            exposedCards.forEach(card => {
                gameState.animation_queue.push({
                    effect: {
                        effect_type: EffectType.CARD_REVEALED,
                        args: { 
                            card_id: card.instance_id,
                            player_id: player.id 
                        }
                    },
                    sourceCard: sourceCard
                });
            });

            if (!specificCardExposed) {
                effectsQueue.unshift([{ effect_type: TriggerType.SUCCESS_PROCESS, args: { ...args, exposed_cards: exposedCards.map(c => c.instance_id), target_card_id: sourceCard.instance_id, card_id: sourceCard.instance_id } }, sourceCard]);
            }
            else {
                effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, exposed_cards: exposedCards.map(c => c.instance_id), target_card_id: sourceCard.instance_id, card_id: sourceCard.instance_id } }, sourceCard]);
            }

        } else if (card_id){
            const exposedCards = [];
            const actual_card_id = card_id === 'self' ? sourceCard.instance_id : card_id;
            const card_to_expose = gameState.all_card_instances[actual_card_id];
            
            if(card_to_expose) {
                exposedCards.push(card_to_expose);
                args.exposed_cards = exposedCards;

                // Record specifically identified exposed cards for the player
                if (!player.exposed_cards) player.exposed_cards = [];
                if (!player.exposed_cards.some(c => c.id === card_to_expose.id)) {
                    player.exposed_cards.push({ ...card_to_expose, exposed_turn: gameState.turn_number });
                }

                if (!gameState.exposed_cards) gameState.exposed_cards = [];
                gameState.exposed_cards.push({ ...card_to_expose, exposed_by: player.id });
            }
            
            if (exposedCards.length > 0) {
                gameState.animation_queue.push({
                        effect: {
                            effect_type: EffectType.CARD_REVEALED,
                            args: { 
                                card_id: actual_card_id,
                                player_id: player.id 
                            }
                        },
                        sourceCard: sourceCard
                    });
                effectsQueue.unshift([{ effect_type: TriggerType.SUCCESS_PROCESS, args: { ...args, exposed_cards: exposedCards.map(c => c.instance_id), target_card_id: sourceCard.instance_id, card_id: sourceCard.instance_id } }, sourceCard]);
            } else {
                effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id, card_id: sourceCard.instance_id } }, sourceCard]);
            }
        } else {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id, card_id: sourceCard.instance_id } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_DISCARD_ALL_IDEOLOGY_FROM_HAND_AND_DECK]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const ideologiesToDiscard = [
            ...player.hand.filter(c => c.card_type === CardType.IDEOLOGY),
            ...player.deck.filter(c => c.card_type === CardType.IDEOLOGY)
        ];

        if (ideologiesToDiscard.length === 0) return;

        for (const card of ideologiesToDiscard) {
            const source_pile = player.hand.some(c => c.instance_id === card.instance_id) ? 'hand' : 'deck';
            effectsQueue.unshift([{ 
                effect_type: EffectType.MOVE_CARD,
                args: {
                    player_id: player_id,
                    card_id: card.instance_id,
                    source_pile: source_pile,
                    destination_pile: 'discard',
                    source_card_id: sourceCard ? sourceCard.instance_id : null,
                }
            }, sourceCard]);
        }
    },
    [EffectType.PROCESS_SET_ALL_SCALE_TO_ZERO_AND_REDUCE_CONSCIOUSNESS]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_ids } = args;
        const targetPlayers = _getTargetPlayers(gameState, sourceCard.owner, player_ids);
        const scalesBefore = new Map();

        for (const player of targetPlayers) {
            if (player) scalesBefore.set(player.id, player.scale);
        }

        for (const player of [...targetPlayers].reverse()) {
            const scaleBefore = scalesBefore.get(player.id);
            if (player && scaleBefore > 0) {
                effectsQueue.unshift([{ effect_type: EffectType.MODIFY_CONSCIOUSNESS_RESERVE, args: { player_id: player.id, amount: -scaleBefore, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
        }

        for (const player of [...targetPlayers].reverse()) {
            if (player && scalesBefore.has(player.id)) {
                 effectsQueue.unshift([{ effect_type: EffectType.SET_SCALE, args: { player_id: player.id, amount: 0, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
        }
    },
    [EffectType.PROCESS_MONEY_CARD_PLACEMENT_EFFECT]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, card_id } = args;
        const player = gameState.players[player_id];
        const playedMoneyCard = player.field.find(c => c.instance_id === card_id);
        if (!player || !playedMoneyCard) return;

        // 33: マネー
        const existingMoneyCards = player.field.filter(c => c.id === 33 && c.instance_id !== card_id);
        if (existingMoneyCards.length > 0) {
            const targetMoneyCard = existingMoneyCards[0];
            const durabilityToAdd = playedMoneyCard.current_durability;
            effectsQueue.unshift([{ effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE, args: { card_id: targetMoneyCard.instance_id, amount: durabilityToAdd, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: playedMoneyCard.instance_id, source_pile: 'field', destination_pile: 'discard', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_MONEY_CARD_TURN_START_EFFECT]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, card_id, condition_durability_ge_30 } = args;
        const player = gameState.players[player_id];
        const moneyCard = player.field.find(c => c.instance_id === card_id);
        if (!player || !moneyCard) return;
        if (condition_durability_ge_30 && moneyCard.current_durability < 30) return;
        
        effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: moneyCard.instance_id, source_pile: 'field', destination_pile: 'hand', maintain: true, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
        
        const sortedHand = [...player.hand].sort((a, b) => a.required_scale - b.required_scale);
        const cardsToDiscard = sortedHand.slice(0, 2);

        if (cardsToDiscard.length > 0) {
            // 37: 資本主義
            effectsQueue.unshift([{ effect_type: EffectType.ADD_CARD_TO_GAME, args: { player_id: player_id, card_template_id: 37, destination_pile: 'hand', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            for (const card of [...cardsToDiscard].reverse()) {
                effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: card.instance_id, source_pile: 'hand', destination_pile: 'discard', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
        }
    },
    [EffectType.PROCESS_DISCARD_ALL_HAND_IDEOLOGY_AND_ADD_MONEY]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const ideologiesInHand = player.hand.filter(c => c.card_type === CardType.IDEOLOGY);
        const discardedCount = ideologiesInHand.length;

        if (discardedCount > 0) {
            // 33: マネー
            effectsQueue.unshift([{ effect_type: EffectType.ADD_CARD_TO_GAME, args: { player_id: player_id, card_template_id: 33, destination_pile: 'hand', initial_durability: discardedCount, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            for (const card of [...ideologiesInHand].reverse()) {
                effectsQueue.unshift([{ effect_type: EffectType.MOVE_CARD, args: { player_id: player_id, card_id: card.instance_id, source_pile: 'hand', destination_pile: 'discard', source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
            }
        }
    },
    [EffectType.PROCESS_MODIFY_MONEY_DURABILITY_RANDOM]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, change_type, value1, value2 } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        // 33: マネー
        const moneyCard = player.field.find(c => c.id === 33);
        if (!moneyCard) return;

        let amount_change = 0;
        if (change_type === 'percent_decrease_or_percent_increase') {
            const is_decrease = Math.random() < 0.5;
            amount_change = is_decrease ? -Math.floor(moneyCard.current_durability * (value1 / 100)) : Math.floor(moneyCard.current_durability * (value2 / 100));
        }

        if (amount_change !== 0) {
            effectsQueue.unshift([{ effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE, args: { card_id: moneyCard.instance_id, amount: amount_change, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_REDUCE_MONEY_DURABILITY_AND_GAIN_SCALE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        // 33: マネー
        const moneyCard = player.field.find(c => c.id === 33);
        if (!moneyCard) return;

        gameState.awaiting_input = {
            type: 'CHOICE_NUMBER',
            player_id: player_id,
            source_card_instance_id: sourceCard.instance_id,
            min: 0,
            max: moneyCard.current_durability,
            prompt: getUILoc('MESSAGES.CONVERT_MONEY_PROMPT', { durability: moneyCard.current_durability }),
            source_effect: { effect_type: 'PROCESS_REDUCE_MONEY_DURABILITY_AND_GAIN_SCALE_RESOLVED', args: { player_id: player_id, money_card_id: moneyCard.instance_id, source_card_id: sourceCard ? sourceCard.instance_id : null } }
        };
    },
    [EffectType.PROCESS_ADD_MONEY_TOKEN_BASED_ON_CARDS_PLAYED]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        if (player && player.cards_played_this_turn > 0) {
            // 33: マネー
            effectsQueue.unshift([{ effect_type: EffectType.ADD_CARD_TO_GAME, args: { player_id: player_id, card_template_id: 33, destination_pile: 'hand', initial_durability: player.cards_played_this_turn, source_card_id: sourceCard ? sourceCard.instance_id : null } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_MONEY_DURABILITY_BASED_COUNT_MODIFY_CARD_DURABILITY]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, target_player_id} = args;
        const player = gameState.players[player_id];
        const target_player = gameState.players[target_player_id];
        if (!player || !target_player) return;

        // 33: マネー
        const moneyCard = player.field.find(c => c.id === 33);
        if (!moneyCard || moneyCard.current_durability <= 0) return;

        const totalDurabilityToAdd = moneyCard.current_durability;
        const wealthCards = target_player.field.filter(c => c.card_type === CardType.WEALTH);

        if (wealthCards.length > 0) {
            // 重みの生成 (2乗を使って適度な偏り＝世知辛さを演出)
            const weights = wealthCards.map(() => Math.pow(Math.random(), 2));
            const totalWeight = weights.reduce((sum, w) => sum + w, 0);

            // 暫定的な分配（切り捨てによる一括計算）
            let remainingDurability = totalDurabilityToAdd;
            const allocations = weights.map(w => {
                const share = Math.floor(totalDurabilityToAdd * (w / totalWeight));
                remainingDurability -= share;
                return share;
            });

            // 端数（切り捨てで余った分）の処理：ランダムな1枚にすべて加算する
            if (remainingDurability > 0) {
                const randomIndex = Math.floor(Math.random() * allocations.length);
                allocations[randomIndex] += remainingDurability;
            }

            allocations.forEach((allocatedAmount, index) => {
                if (allocatedAmount > 0) {
                    const targetCard = wealthCards[index];
                    effectsQueue.unshift([{ 
                        effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE, 
                        args: { 
                            card_id: targetCard.instance_id, 
                            amount: allocatedAmount,
                            source_card_id: sourceCard ? sourceCard.instance_id : null 
                        } 
                    }, sourceCard]);
                }
            });
        }
        
        effectsQueue.push([{ 
            effect_type: EffectType.MOVE_CARD, 
            args: { 
                player_id: player_id, 
                card_id: moneyCard.instance_id, 
                source_pile: 'field', 
                destination_pile: 'discard', 
                source_card_id: sourceCard ? sourceCard.instance_id : null 
            } 
        }, sourceCard]);
    },
    PROCESS_REDUCE_MONEY_DURABILITY_AND_GAIN_SCALE_RESOLVED: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, money_card_id, amount, source_card_id } = args;
        if (amount > 0) {
            effectsQueue.unshift([{ effect_type: EffectType.MODIFY_SCALE_RESERVE, args: { player_id: player_id, amount: amount, source_card_id: source_card_id } }, sourceCard]);
            effectsQueue.unshift([{ effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE, args: { card_id: money_card_id, amount: -amount, source_card_id: source_card_id } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_ADD_CARDS_BASED_ON_DISCARDED_COUNT]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, card_template_id, card_template_name, destination_pile, card_type_to_discard } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const cardsToDiscard = player.hand.filter(c => c.card_type === card_type_to_discard);
        const discardedCount = cardsToDiscard.length;

        if (discardedCount > 0) {
            for (let i = 0; i < discardedCount; i++) {
                effectsQueue.unshift([{
                    effect_type: EffectType.ADD_CARD_TO_GAME,
                    args: {
                        player_id: player_id,
                        card_template_id: card_template_id,
                        card_template_name: card_template_name,
                        destination_pile: destination_pile,
                    }
                }, sourceCard]);
            }

            for (const card of cardsToDiscard) {
                effectsQueue.unshift([{
                    effect_type: EffectType.MOVE_CARD,
                    args: {
                        player_id: player_id,
                        card_id: card.instance_id,
                        source_pile: 'hand',
                        destination_pile: 'discard',
                    }
                }, sourceCard]);
            }
        }
    },
    [EffectType.PROCESS_ADD_CARD_CONDITIONAL]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, condition_target, threshold, card_template_id, card_template_name, is_above = true } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        const value = player[condition_target];
        const conditionMet = is_above ? value >= threshold : value < threshold;

        if (conditionMet) {
            effectsQueue.unshift([{
                effect_type: EffectType.ADD_CARD_TO_GAME,
                args: {
                    player_id: player_id,
                    card_template_id: card_template_id,
                    card_template_name: card_template_name,
                    destination_pile: 'hand',
                    source_card_id: sourceCard ? sourceCard.instance_id : null,
                }
            }, sourceCard]);
        }
    },
    [EffectType.PROCESS_ADD_CHOICE_CARD_TO_HAND]: (gameState, args, cardDefs, sourceCard) => {
        const { player_id, options } = args;
        gameState.awaiting_input = { type: 'CHOICE_CARD_TO_ADD', options, player_id, source_card_instance_id: sourceCard.instance_id };
    },
    [EffectType.PROCESS_CHOOSE_AND_MOVE_CARD_FROM_PILE]: (gameState, args, cardDefs, sourceCard) => {
        let { player_id, source_piles, destination_pile, card_type, position } = args;

        if (source_piles && source_piles.includes('discard_pile')) {
            source_piles = source_piles.map(p => p === 'discard_pile' ? 'discard' : p);
        }

        const player = gameState.players[player_id];
        if (!player) return;

        let candidateCards = [];
        source_piles.forEach(pileName => {
            const pile = player[pileName];
            if (pile) {
                const cardsInPile = Array.isArray(pile) ? pile : [pile];
                candidateCards.push(...cardsInPile.filter(c => !card_type || c.card_type === card_type));
            }
        });

        if (card_type) candidateCards = candidateCards.filter(c => c.card_type === card_type);

        if (candidateCards.length > 0) {
            gameState.awaiting_input = { type: 'CHOICE_CARD_FROM_PILE', options: candidateCards, destination_pile, player_id, source_card_instance_id: sourceCard.instance_id, source_piles, position };
        } 
    },
    [EffectType.PROCESS_CHOOSE_AND_MODIFY_DURABILITY_TO_WEALTH]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, target_player_id } = args;
        const targetPlayer = gameState.players[target_player_id];
        if (!targetPlayer) {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
            return;
        };

        const candidateCards = targetPlayer.field.filter(c => c.card_type === CardType.WEALTH);
        if (candidateCards.length > 0) {
            gameState.awaiting_input = { 
                type: 'CHOICE_CARD_FOR_EFFECT', 
                options: candidateCards, 
                player_id, 
                source_card_instance_id: sourceCard.instance_id, 
                source_card_name: getCardName(sourceCard.id),
                source_effect: { 
                    effect_type: 'PROCESS_CHOOSE_AND_MODIFY_DURABILITY_TO_WEALTH_RESOLVED', 
                    args: { ...args } 
                } 
            };
        } else {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_RETURN_LARGEST_REQUIRED_SCALE_CARD_TO_DECK]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id } = args;
        const player = gameState.players[player_id];
        if (!player || player.hand.length === 0) {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
            return;
        }

        const cardsToReturn = _selectCards(gameState, player, player.hand, 'highest_required_scale', 1, sourceCard, args);

        if (cardsToReturn && cardsToReturn.length > 0) {
            const cardToReturn = cardsToReturn[0];

            effectsQueue.unshift([{
                effect_type: TriggerType.SUCCESS_PROCESS,
                args: { ...args, returned_card_id: cardToReturn.instance_id, target_card_id: sourceCard.instance_id, card_id: sourceCard.instance_id }
            }, sourceCard]);

            effectsQueue.unshift([{
                effect_type: EffectType.MOVE_CARD,
                args: {
                    player_id: player_id,
                    card_id: cardToReturn.instance_id,
                    source_pile: 'hand',
                    destination_pile: 'deck',
                    position: 'random'
                }
            }, sourceCard]);
        } else {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
        }
    },
    
    PROCESS_CHOOSE_AND_MODIFY_DURABILITY_TO_WEALTH_RESOLVED: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { card_id, amount, bonus_effect_if_money, bonus_scale_amount, player_id } = args;
        
        let targetCard = null;
        for (const p of Object.values(gameState.players)) {
            const card = p.field.find(c => c.instance_id === card_id);
            if (card) { targetCard = card; break; }
        }

        if (targetCard) {
            effectsQueue.unshift([{ 
                effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE, 
                args: { card_id: targetCard.instance_id, amount: amount, source_card_id: sourceCard ? sourceCard.instance_id : null } 
            }, sourceCard]);

            // 33: マネー
            if (bonus_effect_if_money && targetCard.id === 33) {
                effectsQueue.unshift([{
                    effect_type: EffectType.MODIFY_SCALE_RESERVE,
                    args: { player_id: player_id, amount: bonus_scale_amount }
                }, sourceCard]);
            }
        } else {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_CHOOSE_AND_BOUNCE_TO_WEALTH]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, target_player_id } = args;
        const targetPlayer = gameState.players[target_player_id];
        if (!targetPlayer) {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
            return;
        }

        const candidateCards = targetPlayer.field.filter(c => c.card_type === CardType.WEALTH);
        if (candidateCards.length > 0) {
            gameState.awaiting_input = { 
                type: 'CHOICE_CARD_FOR_EFFECT', 
                options: candidateCards, 
                player_id, 
                source_card_instance_id: sourceCard.instance_id, 
                source_card_name: getCardName(sourceCard.id),
                source_effect: { 
                    effect_type: 'PROCESS_CHOOSE_AND_BOUNCE_TO_WEALTH_RESOLVED', 
                    args: { ...args } 
                } 
            };
        } else {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_id } }, sourceCard]);
        }
    },

    PROCESS_CHOOSE_AND_BOUNCE_TO_WEALTH_RESOLVED: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { card_id, target_player_id } = args;
        
        if (card_id) {
            effectsQueue.unshift([{ 
                effect_type: EffectType.MOVE_CARD, 
                args: { 
                    player_id: target_player_id, 
                    card_id: card_id, 
                    source_pile: 'field', 
                    destination_pile: 'hand' 
                } 
            }, sourceCard]);

            effectsQueue.unshift([{ 
                effect_type: TriggerType.SUCCESS_PROCESS, 
                args: { ...args, target_card_id: sourceCard.instance_id, card_id: sourceCard.instance_id }
            }, sourceCard]);
        } else {
            effectsQueue.unshift([{ effect_type: TriggerType.FAILED_PROCESS, args: { ...args, target_card_id: sourceCard.instance_instance_id } }, sourceCard]);
        }
    },
    [EffectType.PROCESS_DRAW_RANDOM_CARD_AND_MODIFY_REQUIRED_SCALE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, card_type, amount, scale_reduction, scale_reduction_percentage, round_down } = args;
        const player = gameState.players[player_id];
        if (!player || !player.deck || player.deck.length === 0) return;

        const matchingCards = player.deck.filter(c => c.card_type === card_type);
        if (matchingCards.length === 0) return;

        const shuffledMatchingCards = shuffle([...matchingCards]);

        for (let i = 0; i < amount; i++) {
            if (i >= shuffledMatchingCards.length) break;
            const cardToDraw = shuffledMatchingCards[i];
            
            let scaleChangeAmount = 0;
            if (scale_reduction) {
                scaleChangeAmount = -scale_reduction;
            } else if (scale_reduction_percentage) {
                let reduction = cardToDraw.required_scale * (scale_reduction_percentage / 100);
                if (round_down) reduction = Math.floor(reduction);
                scaleChangeAmount = -reduction;
            }

            if (scaleChangeAmount !== 0) {
                effectsQueue.unshift([{
                    effect_type: EffectType.MODIFY_CARD_REQUIRED_SCALE,
                    args: {
                        card_id: cardToDraw.instance_id,
                        amount: scaleChangeAmount,
                        min_value: 0
                    }
                }, sourceCard]);
            }
            
            effectsQueue.unshift([{
                effect_type: EffectType.MOVE_CARD,
                args: {
                    player_id: player_id,
                    card_id: cardToDraw.instance_id,
                    source_pile: 'deck',
                    destination_pile: 'hand',
                }
            }, sourceCard]);
        }
    },
    [EffectType.PROCESS_COUNTER_ATTACK]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { counter_damage, triggering_effect_args } = args;
        const attacker_card_id = triggering_effect_args ? triggering_effect_args.source_card_id : null;

        if (attacker_card_id && counter_damage) {
            effectsQueue.unshift([{
                effect_type: EffectType.MODIFY_CARD_DURABILITY_RESERVE,
                args: {
                    card_id: attacker_card_id,
                    amount: -counter_damage,
                    source_card_id: sourceCard ? sourceCard.instance_id : null
                }
            }, sourceCard]);
        }
    },
    // 再開発計画：場の財カードを1枚ゲームから除外する（プレイヤー・NPC共通）
    [EffectType.PROCESS_REDEVELOPMENT_PLAN]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, card_id } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        // 対象カードが場の財であることを確認
        const targetCard = player.field.find(c => c.instance_id === card_id);
        if (!targetCard) return;

        // 1ターン1度の使用制限を記録
        player.redevelopment_used = true;

        // カードをゲームから除外
        effectsQueue.unshift([{
            effect_type: EffectType.REMOVE_CARD_FROM_GAME,
            args: {
                player_id: player_id,
                card_id: card_id,
                source_card_id: sourceCard ? sourceCard.instance_id : null
            }
        }, sourceCard]);
    },

    // 予感専用ドロー：過去に引いた定義IDのカードを除いた仮想pileから指定枚数引く
    [EffectType.PROCESS_YOCAN_DRAW]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { player_id, count = 1 } = args;
        const player = gameState.players[player_id];
        if (!player) return;

        // yocan_drawn_card_ids が未初期化の場合に備えて初期化
        if (!player.yocan_drawn_card_ids) {
            player.yocan_drawn_card_ids = [];
        }

        // 除外済み定義IDのセット
        const excludedIds = new Set(player.yocan_drawn_card_ids);

        // デッキから除外対象を除いた仮想pile（順序維持）
        const virtualPile = player.deck.filter(card => !excludedIds.has(card.id));

        // 引く枚数分だけ先頭から対象を確定
        const drawTargets = virtualPile.slice(0, count);

        if (drawTargets.length === 0) return;

        // 引いた定義IDを記録（重複なし）
        for (const card of drawTargets) {
            if (!excludedIds.has(card.id)) {
                player.yocan_drawn_card_ids.push(card.id);
                excludedIds.add(card.id);
            }
        }

        // 各対象カードをMOVE_CARD（instance_id直接指定、source_pile: 'deck'）でキューに積む
        // 後ろから unshift することで元の順序通りに処理される
        for (let i = drawTargets.length - 1; i >= 0; i--) {
            effectsQueue.unshift([{
                effect_type: EffectType.MOVE_CARD,
                args: {
                    player_id: player_id,
                    card_id: drawTargets[i].instance_id,
                    source_pile: 'deck',
                    destination_pile: 'hand',
                }
            }, sourceCard]);
        }
    },

    [EffectType.SKIP_EFFECT]: (gameState, args) => {
        gameState.effects_to_skip[args.effect_type] = args.player_id;
    },
    [EffectType.ADD_MODIFY_PARAMETER_CORRECTION]: (gameState, args) => {
        const player = gameState.players[args.player_id];
        if (player) {
            if (!player.modify_parameter_corrections) {
                player.modify_parameter_corrections = [];
            } else {
                if (player.modify_parameter_corrections.some(correction =>
                    correction.source_card_id === args.source_card_id &&
                    correction.correct_target === args.correct_target &&
                    correction.correct_direction === args.correct_direction &&
                    correction.correct_type === args.correct_type &&
                    correction.amount === args.amount)) {
                    return;
                } else {
                    const newCorrection = { 
                        correct_target: args.correct_target, 
                        correct_direction: args.correct_direction, 
                        correct_type: args.correct_type, 
                        amount: args.amount,
                        source_card_id: args.source_card_id
                    };
                    player.modify_parameter_corrections.push(newCorrection);
                }
            }
        }
    },

    [EffectType.MODIFY_CONSCIOUSNESS_RESERVE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        let resolvedArgs = { ...args };
        const player = gameState.players[args.player_id];

        if (player && args.amount_percentage) {
            const originalAmount = player.consciousness;
            const calcValue = originalAmount * (Math.abs(args.amount_percentage) / 100);
            const roundedChange = args.round_down ? Math.floor(calcValue) : Math.round(calcValue);
            
            if (args.store_original_value_for_temp) {
                gameState.temp_effect_data[args.store_original_value_for_temp] = roundedChange;
            }

            resolvedArgs.amount = (args.amount_percentage < 0 ? -roundedChange : roundedChange) + (args.amount || 0);
        } else if (args.amount_based_on_removed_discard_count) {
            resolvedArgs.amount = gameState.temp_effect_data.removed_discard_count || 0;
        } else if (args.amount_based_on_temp_value) {
            let amount = gameState.temp_effect_data[args.amount_based_on_temp_value] || 0;
            if (args.is_negative) {
                amount = -amount;
            }
            resolvedArgs.amount = amount;
        } else if (args.amount_based_on_hand_count) {
            resolvedArgs.amount = player.hand.length;
        }
        
        resolvedArgs.target_player_id = resolvedArgs.player_id;
        
        if (sourceCard && !resolvedArgs.source_card_id) {
            resolvedArgs.source_card_id = sourceCard.instance_id;
        }
        
        const effects_to_add = [];
        effects_to_add.push([{ effect_type: EffectType.MODIFY_CONSCIOUSNESS, args: resolvedArgs }, sourceCard]);

        if (resolvedArgs.amount > 0) {
            effects_to_add.push([{ effect_type: TriggerType.MODIFY_CONSCIOUSNESS_INCREASE_RESERVE_OWNER, args: resolvedArgs }, sourceCard]);
        } else if (resolvedArgs.amount < 0) {
            effects_to_add.push([{ effect_type: TriggerType.MODIFY_CONSCIOUSNESS_DECREASE_RESERVE_OWNER, args: resolvedArgs }, sourceCard]);
            effects_to_add.push([{ effect_type: TriggerType.MODIFY_CONSCIOUSNESS_DECREASE_RESERVE_OPPONENT, args: resolvedArgs }, sourceCard]);
        }
        effectsQueue.unshift(...effects_to_add.reverse());
    },
    [EffectType.MODIFY_SCALE_RESERVE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        let resolvedArgs = { ...args };
        const player = gameState.players[args.player_id];

        if (player && args.amount_percentage) {
            const originalAmount = player.scale;
            const calcValue = originalAmount * (Math.abs(args.amount_percentage) / 100);
            const roundedChange = args.round_down ? Math.floor(calcValue) : Math.round(calcValue);
            
            if (args.store_original_value_for_temp) {
                gameState.temp_effect_data[args.store_original_value_for_temp] = roundedChange;
            }

            resolvedArgs.amount = (args.amount_percentage < 0 ? -roundedChange : roundedChange) + (args.amount || 0);
        }

        resolvedArgs.target_player_id = resolvedArgs.player_id;
        
        if (sourceCard && !resolvedArgs.source_card_id) {
            resolvedArgs.source_card_id = sourceCard.instance_id;
        }
        
        const effects_to_add = [];
        effects_to_add.push([{ effect_type: EffectType.MODIFY_SCALE, args: resolvedArgs }, sourceCard]);

        if (resolvedArgs.amount > 0) {
            effects_to_add.push([{ effect_type: TriggerType.MODIFY_SCALE_INCREASE_RESERVE_OWNER, args: resolvedArgs }, sourceCard]);
        } else if (resolvedArgs.amount < 0) {
            effects_to_add.push([{ effect_type: TriggerType.MODIFY_SCALE_DECREASE_RESERVE_OWNER, args: resolvedArgs }, sourceCard]);
        }
        effectsQueue.unshift(...effects_to_add.reverse());
    },
    [EffectType.MODIFY_CARD_DURABILITY_RESERVE]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        let resolved_args = { ...args };

        if (resolved_args.amount_based_on_self_durability && sourceCard) {
            const source_durability = sourceCard.current_durability !== undefined ? sourceCard.current_durability : sourceCard.durability;
            if (resolved_args.amount_based_on_self_durability === 'minus') {
                resolved_args.amount = -source_durability;
            }
            delete resolved_args.amount_based_on_self_durability;
        }
        
        const newArgs = { ...resolved_args, card_id: args.card_id };
        const effects_to_add = [];
        effects_to_add.push([{ effect_type: EffectType.MODIFY_CARD_DURABILITY, args: newArgs, target_card_id: args.card_id }, sourceCard]);

        const targetCardForTrigger = gameState.all_card_instances[args.card_id];
        if (targetCardForTrigger) {
            const triggerArgs = { ...newArgs, target_player_id: targetCardForTrigger.owner };
            if (newArgs.amount > 0) {
                effects_to_add.push([{ effect_type: TriggerType.MODIFY_CARD_DURABILITY_INCREASE_RESERVE_OWNER, args: triggerArgs }, sourceCard]);
            } else if (newArgs.amount < 0) {
                effects_to_add.push([{ effect_type: TriggerType.MODIFY_CARD_DURABILITY_DECREASE_RESERVE_OWNER, args: triggerArgs }, sourceCard]);
            }
        }

        effectsQueue.unshift(...effects_to_add.reverse());
    },
    
    [EffectType.MOVE_CARD_AFTER_ANIMATION]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        const { card_id } = args;
        const targetCard = gameState.all_card_instances[card_id];
        
        if (!targetCard) return;
        
        if (gameState.animationCallbacks) {
            gameState.animationCallbacks.set(card_id, () => {
                effectsQueue.unshift([{ 
                    effect_type: EffectType.MOVE_CARD, 
                    args: {
                        player_id: args.player_id,
                        card_id: args.card_id,
                        source_pile: args.source_pile,
                        destination_pile: args.destination_pile,
                        source_card_id: args.source_card_id
                    }
                }, sourceCard]);
            });
        } else {
            effectsQueue.unshift([{ 
                effect_type: EffectType.MOVE_CARD, 
                args: {
                    player_id: args.player_id,
                    card_id: args.card_id,
                    source_pile: args.source_pile,
                    destination_pile: args.destination_pile,
                    source_card_id: args.source_card_id
                }
            }, sourceCard]);
        }
    },
    
    [EffectType.TRIGGER_AFTER_ANIMATION]: (gameState, args, cardDefs, sourceCard, effectsQueue) => {
        if (gameState.animationCallbacks) {
            const callbackId = `trigger-${Date.now()}-${Math.random()}`;
            gameState.animationCallbacks.set(callbackId, () => {
                effectsQueue.unshift([{ 
                    effect_type: args.trigger_type, 
                    args: args.trigger_args
                }, sourceCard]);
            });
        } else {
            effectsQueue.unshift([{ 
                effect_type: args.trigger_type, 
                args: args.trigger_args
            }, sourceCard]);
        }
    },
    
    [EffectType.TURN_START]: () => {},
    [EffectType.TURN_END]: () => {},
    [EffectType.GAME_RESULT]: () => {},
    [EffectType.TURN_ORDER_DECISION]: () => {},

    [EffectType.REORDER_FIELD_CARDS]: (gameState, args) => {
        const { player_id, card_id, position } = args;
        const player = gameState.players[player_id];
        if (!player || !player.field) return;

        const cardIndex = player.field.findIndex(c => c.instance_id === card_id);
        if (cardIndex === -1) return;

        const [cardToMove] = player.field.splice(cardIndex, 1);

        if (position === 'leftmost') {
            player.field.unshift(cardToMove);
        } else if (position === 'rightmost') {
            player.field.push(cardToMove);
        }
        
        gameState.animation_queue.push({
            effect: {
                effect_type: 'FIELD_CARDS_REORDERED',
                args: {
                    player_id: player_id,
                    card_id: card_id,
                    position: position
                }
            },
            sourceCard: null
        });
    },
    [EffectType.PRESENT_EVENT_CARD]: (gameState, args) => {
        gameState.animation_queue.push({
            effect: {
                effect_type: EffectType.EVENT_CARD_PLAYED,
                args: { card_id: args.card_id, player_id: args.player_id }
            },
            sourceCard: gameState.all_card_instances[args.card_id]
        });
    },

    [EffectType.CHECK_GAME_OVER]: () => {},
};

const checkAllReactions = (processedEffect, sourceCard, gameState) => {
    const newEffects = [];
    const allCards = [];

    const currentPlayerId = gameState.current_turn;
    const playerIds = Object.keys(gameState.players);
    const otherPlayerId = playerIds.find(id => id !== currentPlayerId);

    const orderedPlayers = [];
    if (currentPlayerId && gameState.players[currentPlayerId]) {
        orderedPlayers.push(gameState.players[currentPlayerId]);
    }
    if (otherPlayerId && gameState.players[otherPlayerId]) {
        orderedPlayers.push(gameState.players[otherPlayerId]);
    }
    
    if (orderedPlayers.length < playerIds.length) {
        playerIds.forEach(id => {
            if (!orderedPlayers.some(p => p.id === id)) {
                orderedPlayers.push(gameState.players[id]);
            }
        });
    }

    orderedPlayers.forEach(player => {
        if (player.ideology) {
            allCards.push(player.ideology);
        }
        allCards.push(...player.field);
        allCards.push(...player.hand);
        allCards.push(...player.discard);
        allCards.push(...player.deck);
    });
    if (sourceCard && sourceCard.location === 'playing_event') {
        allCards.push(sourceCard);
    }
    const filteredCards = allCards.filter(card => card && card.card_type);

    for (const card of filteredCards) {
        const reactionEffects = checkCardReaction(card, processedEffect, gameState);
        if (reactionEffects.length > 0) {
            for (const reactionEffect of reactionEffects) {
                reactionEffect.args = {
                    ...reactionEffect.args,
                    triggering_source_card_id: processedEffect.source_card_id || (sourceCard ? sourceCard.instance_id : null),
                    triggering_effect_args: processedEffect.args,
                };
                newEffects.push([reactionEffect, card]);
            }
        }
    }
    return newEffects;
};

export const setEffectLogger = (logger) => {
    globalEffectLogger = logger;
};

const shouldLogEffectInternal = (effect) => {
    if (!effect || !effect.effect_type) return false;

    const excludedEffects = [
        'TURN_START', 'TURN_END', 'GAME_RESULT', 'TURN_ORDER_DECISION', 
        'LIMIT_WARNING', 'EFFECT_NULLIFIED',
        'ADD_MODIFY_PARAMETER_CORRECTION', 'REMOVE_MODIFY_PARAMETER_CORRECTION', 'CLEAR_MODIFY_PARAMETER_CORRECTIONS',
        'TRIGGER_EFFECT', 'CHECK_REACTION', 'CHECK_GAME_OVER',
        EffectType.CONSCIOUSNESS_CHANGED, EffectType.SCALE_CHANGED, EffectType.CARD_DURABILITY_CHANGED,
        EffectType.SET_CONSCIOUSNESS, EffectType.SET_SCALE,
        'MODIFY_CONSCIOUSNESS_RESERVE', 'MODIFY_SCALE_RESERVE', 'MODIFY_CARD_DURABILITY_RESERVE',
        'MODIFY_CONSCIOUSNESS', 'MODIFY_SCALE', 'MODIFY_CARD_DURABILITY',
        'SKIP_EFFECT',
        
        TriggerType.START_TURN, TriggerType.END_TURN_OWNER, TriggerType.END_TURN_OPPONENT,
        TriggerType.PLAY_EVENT, TriggerType.PLAY_EVENT_OWNER, TriggerType.PLAY_EVENT_OPPONENT, TriggerType.PLAY_EVENT_THIS,
        TriggerType.PLAYER_PLAY_CARD_ACTION, TriggerType.PLAYER_PLAY_CARD_ACTION_OWNER, TriggerType.PLAYER_PLAY_CARD_ACTION_OPPONENT,
        TriggerType.CARD_ADDED_TO_HAND_THIS, TriggerType.CARD_ADDED_TO_HAND, TriggerType.CARD_ADDED_TO_HAND_OWNER,
        TriggerType.CARD_DRAWN_THIS, TriggerType.CARD_DRAWN, TriggerType.CARD_DRAWN_OWNER,
        TriggerType.CARD_PLACED_THIS, TriggerType.CARD_PLACED, TriggerType.CARD_PLACED_OWNER, TriggerType.CARD_PLACED_OPPONENT,
        TriggerType.CARD_DISCARDED_THIS, TriggerType.CARD_DISCARDED, TriggerType.CARD_DISCARDED_OWNER,
        TriggerType.DAMAGE_THIS, TriggerType.BOOST_THIS,
        TriggerType.WEALTH_DURABILITY_ZERO, TriggerType.WEALTH_DURABILITY_ZERO_OWNER, TriggerType.WEALTH_DURABILITY_ZERO_OPPONENT, TriggerType.WEALTH_DURABILITY_ZERO_THIS,
        TriggerType.MODIFY_CONSCIOUSNESS, TriggerType.MODIFY_CONSCIOUSNESS_DECREASE_RESERVE_OWNER, TriggerType.MODIFY_CONSCIOUSNESS_DECREASE_RESERVE_OPPONENT, TriggerType.MODIFY_CONSCIOUSNESS_INCREASE_RESERVE_OWNER,
        TriggerType.MODIFY_SCALE_DECREASE_RESERVE_OWNER, TriggerType.MODIFY_SCALE_INCREASE_RESERVE_OWNER,
        TriggerType.MODIFY_CARD_DURABILITY_DECREASE_RESERVE_OWNER, TriggerType.MODIFY_CARD_DURABILITY_INCREASE_RESERVE_OWNER,
        TriggerType.SUCCESS_PROCESS, TriggerType.FAILED_PROCESS,
    ];

    if (excludedEffects.includes(effect.effect_type)) return false;
    
    const hasFormatter = logEntryGenerator.getAvailableFormatters().includes(effect.effect_type);
    if (hasFormatter) return true;

    const explicitlyIncludedEffects = [
        'PLAYER_ACTION', 'REMOVE_CARD_FROM_GAME', 'PROCESS_CARD_OPERATION',
        'PROCESS_DEAL_DAMAGE_TO_ALL_WEALTH', 'PROCESS_CHOOSE_AND_DISCARD_IDEOLOGY',
        'PROCESS_DISCARD_ALL_HAND_WEALTH_CARDS_AND_DRAW', 'PROCESS_MOVE_HAND_WEALTH_TO_DECK_AND_DRAW',
        'PROCESS_EXPOSE_CARD_BY_TYPE', 'PROCESS_DISCARD_ALL_IDEOLOGY_FROM_HAND_AND_DECK',
        'PROCESS_SET_ALL_SCALE_TO_ZERO_AND_REDUCE_CONSCIOUSNESS', 'PROCESS_MONEY_CARD_PLACEMENT_EFFECT',
        'PROCESS_MONEY_CARD_TURN_START_EFFECT', 'PROCESS_DISCARD_ALL_HAND_IDEOLOGY_AND_ADD_MONEY',
        'PROCESS_MODIFY_MONEY_DURABILITY_RANDOM', 'PROCESS_REDUCE_MONEY_DURABILITY_AND_GAIN_SCALE',
        'PROCESS_ADD_MONEY_TOKEN_BASED_ON_CARDS_PLAYED', 'PROCESS_MONEY_DURABILITY_BASED_COUNT_MODIFY_CARD_DURABILITY',
        'PROCESS_ADD_CARDS_BASED_ON_DISCARDED_COUNT', 'PROCESS_ADD_CARD_CONDITIONAL',
        'PROCESS_ADD_CHOICE_CARD_TO_HAND', 'PROCESS_CHOOSE_AND_MOVE_CARD_FROM_PILE',
        'PROCESS_CHOOSE_AND_MODIFY_DURABILITY_TO_WEALTH', 'PROCESS_RETURN_LARGEST_REQUIRED_SCALE_CARD_TO_DECK',
        'PROCESS_CHOOSE_AND_BOUNCE_TO_WEALTH',
        'PROCESS_DRAW_RANDOM_CARD_AND_MODIFY_REQUIRED_SCALE', 'PROCESS_COUNTER_ATTACK', 'REORDER_FIELD_CARDS',
        'PROCESS_YOCAN_DRAW', 'PROCESS_REDEVELOPMENT_PLAN',
    ];
    
    return explicitlyIncludedEffects.includes(effect.effect_type);
};

export const processEffects = (gameState) => {
    const stateAfterImmediateEffects = produce(gameState, draftState => {
        if (draftState.game_over) return;
        let safetyBreak = 0;
        
        while (draftState.effect_queue.length > 0 && !draftState.awaiting_input && !draftState.game_over && safetyBreak < 500) {
            const [effect, sourceCard] = draftState.effect_queue.shift();
            
            if (globalEffectLogger) {
                globalEffectLogger.recordEffect(draftState, effect, sourceCard);
            }
            
            if (draftState.effects_to_skip && draftState.effects_to_skip[effect.effect_type]) {
                const skippablePlayerId = draftState.effects_to_skip[effect.effect_type];
                if (effect.args.player_id === skippablePlayerId) {
                    delete draftState.effects_to_skip[effect.effect_type];
                    if (effect.args.card_id) {
                        draftState.animation_queue.push({
                            effect: {
                                effect_type: 'EFFECT_NULLIFIED',
                                args: {
                                    target_card_id: effect.args.card_id,
                                    reason: 'skip_effect',
                                    message: getUILoc('MESSAGES.EFFECT_SKIPPED')
                                }
                            },
                            sourceCard: sourceCard
                        });
                    }
                    safetyBreak++;
                    continue;
                }
            }

            const handler = effectHandlers[effect.effect_type];
            if (handler) {
                handler(draftState, effect.args, draftState.cardDefs, sourceCard, draftState.effect_queue);
                 if (draftState.awaiting_input) return;
            }

            const reactionEffects = checkAllReactions(effect, sourceCard, draftState);
            if (reactionEffects.length > 0) {
                draftState.effect_queue.unshift(...reactionEffects);
            }

            if (shouldLogEffectInternal(effect)) {
                const logEntry = logEntryGenerator.generateEntry(
                    effect.effect_type,
                    effect.args || {},
                    sourceCard,
                    draftState
                );
                if (logEntry) draftState.game_log.push(logEntry);
            }

            _updateGameOverState(draftState);

            safetyBreak++;
            if (safetyBreak >= 500) {
                draftState.game_over = true;
                draftState.game_over_reason = getUILoc('MESSAGES.SAFETY_BREAK');
            }
        }
            
        if (globalEffectLogger && globalEffectLogger.notifyEffectProcessingComplete) {
            globalEffectLogger.notifyEffectProcessingComplete();
        }
    });

    return stateAfterImmediateEffects;
};